import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/supabase'

type AccountTemplate = Database['public']['Tables']['account_templates']['Row']

interface UseTemplatesReturn {
  templates: AccountTemplate[]
  loading: boolean
  error: string | null
  getTemplateByShortcut: (shortcut: string) => AccountTemplate | undefined
  refetch: () => Promise<void>
}

export const useTemplates = (): UseTemplatesReturn => {
  const { user } = useAuth()
  const [templates, setTemplates] = useState<AccountTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchTemplates = async (): Promise<void> => {
    if (!user?.account_id || !supabase) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data: templatesData, error: templatesError } = await supabase
        .from('account_templates')
        .select('*')
        .eq('account_id', user.account_id)
        .eq('is_active', true)
        .order('name', { ascending: true })

      if (templatesError) throw templatesError

      setTemplates(templatesData || [])
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar templates')
    } finally {
      setLoading(false)
    }
  }

  const getTemplateByShortcut = (shortcut: string): AccountTemplate | undefined => {
    return templates.find(t => t.shortcut === shortcut)
  }

  const refetch = async (): Promise<void> => {
    await fetchTemplates()
  }

  useEffect(() => {
    if (!user?.account_id || !supabase) {
      return
    }

    console.log('🔄 [useTemplates] Setting up templates subscription')

    fetchTemplates()

    const channel = supabase
      .channel('templates-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_templates',
          filter: `account_id=eq.${user.account_id}`
        },
        (payload) => {
          console.log('📝 [useTemplates] Template change detected:', payload.eventType)

          if (payload.eventType === 'INSERT') {
            const newTemplate = payload.new as AccountTemplate
            if (newTemplate.is_active) {
              setTemplates(prev => {
                const exists = prev.some(t => t.id === newTemplate.id)
                if (exists) return prev
                console.log('✅ [useTemplates] Adding new template')
                return [...prev, newTemplate].sort((a, b) => a.name.localeCompare(b.name))
              })
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedTemplate = payload.new as AccountTemplate
            setTemplates(prev => {
              console.log('🔄 [useTemplates] Updating template:', updatedTemplate.id)
              if (!updatedTemplate.is_active) {
                return prev.filter(t => t.id !== updatedTemplate.id)
              }
              return prev.map(t => t.id === updatedTemplate.id ? updatedTemplate : t)
                .sort((a, b) => a.name.localeCompare(b.name))
            })
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as AccountTemplate).id
            setTemplates(prev => {
              console.log('🗑️ [useTemplates] Removing template:', deletedId)
              return prev.filter(t => t.id !== deletedId)
            })
          }
        }
      )
      .subscribe((status) => {
        console.log('🔌 [useTemplates] Subscription status:', status)
      })

    return () => {
      console.log('🔌 [useTemplates] Unsubscribing from templates')
      if (supabase) {
        supabase.removeChannel(channel)
      }
    }
  }, [user?.account_id])

  return {
    templates,
    loading,
    error,
    getTemplateByShortcut,
    refetch,
  }
}
